import { api } from '@/api';
import { patientsApi } from '@/features/patients';
import { ActionContext } from 'vuex';
import { IPatientCreate, IPatientUpdate, IAttachment } from '@/interfaces';
import { IPatientCommentCreate, IPatientCommentUpdate } from '@/features/patients/patient-comments/types';
import { State } from '../state';
import { PatientState } from './state';
import { readOnePatient } from './getters';
import { getStoreAccessors } from 'typesafe-vuex';
import { commitSetDocumentsForPatient, commitSetPatient, commitSetPatients, commitSetComment, commitSetComments, commitSetMinimalPatient } from './mutations';
import { dispatchCheckApiError } from '../main/actions';
import { commitAddNotification, commitRemoveNotification } from '../main/mutations';
import { parse_error_message } from './utils'
import { i18n } from '@/plugins/i18n';
import {
  IPrescriptionInput,
} from '@/interfaces/eagreement';
import {
  ISendEFactInput, ICreditNoteInput
} from '@/interfaces/efact';
type MainContext = ActionContext<PatientState, State>;

export const actions = {
  // Patients
  async actionClearPatients(context: MainContext) {
    // initialize to empty list
    commitSetPatients(context, [])
  },
  async actionGetPatient(context: MainContext, payload: { patientId: number }) {
    const response = await api.getPatient(payload.patientId);
    if (response) {
      commitSetPatient(context, response.data);
    }
  },
  async actionGetMinimalPatient(context: MainContext, payload: { patientId: number }) {
    const response = await patientsApi.getOneMinimal(payload.patientId);
    if (response) {
      commitSetMinimalPatient(context, response.data);
    }
  },
  async actionUpdatePatient(context: MainContext, payload: { id: number, patient: IPatientUpdate }) {
    try {
      const response = (await Promise.all([
        api.updatePatient(payload.id, payload.patient),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetPatient(context, response.data);
      commitAddNotification(context, { content: String(i18n.t('patients.patientUpdated')), color: 'success' });
    } catch (error: any) {
      if (error.response && error.response.status == 422) {
        const detail = error.response.data?.detail;
        const message = Array.isArray(detail) ? detail[0]?.msg : detail;
        commitAddNotification(context, { color: 'error', content: message ?? String(i18n.t('common.errorOccurred')) });
      }
      else if (error.response) {
        commitAddNotification(context, { color: 'error', content: error.response.data.detail });
      }
    }
  },
  async actionDisablePatient(context: MainContext, payload: { id: number }) {
    try {
      await Promise.all([
        api.disablePatient(payload.id),
        new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      commitAddNotification(context, { content: String(i18n.t('patients.patientArchived')), color: 'success' });
    } catch (error: any) {
      if (error.response) {
        commitAddNotification(context, { color: 'error', content: error.response.data.detail });
      }
    }
  },
  async actionCreatePatient(context: MainContext, payload: IPatientCreate) {
    try {
      const response = (await Promise.all([
        api.createPatient(payload),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetPatient(context, response.data);
      commitAddNotification(context, { content: String(i18n.t('patients.patientCreated')), color: 'success' });
      return response.data
    } catch (error: any) {
      if (error.response && error.response.status == 422) {
        commitAddNotification(context, { color: 'error', content: error.response.data.detail[0].msg });
      }
      else if (error.response) {
        commitAddNotification(context, { color: 'error', content: error.response.data.detail });
      }
    }
  },
  // PatientComments
  async actionGetComments(context: MainContext, payload: { patientId?: number }) {
    try {
      const response = await api.getPatientComments(payload.patientId);
      if (response) {
        commitSetComments(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdateComment(context: MainContext, payload: { id: number, comment: IPatientCommentUpdate }) {
    try {
      const response = (await Promise.all([
        api.updatePatientComment(payload.id, payload.comment),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetComment(context, response.data);
      commitAddNotification(context, { content: String(i18n.t('patients.commentUpdated')), color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionDeleteComment(context: MainContext, payload: { id: number }) {
    try {
      await Promise.all([
        api.deletePatientComment(payload.id),
        new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      commitAddNotification(context, { content: String(i18n.t('patients.commentDeleted')), color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCreateComment(context: MainContext, payload: IPatientCommentCreate) {
    try {
      const response = (await Promise.all([
        api.createPatientComment(payload),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetComment(context, response.data);
      commitAddNotification(context, { content: String(i18n.t('patients.commentCreated')), color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  // ehealth
  async queryMemberEHealth(context: MainContext, payload: { ssin: string | null, io: string | null, io_membership: string | null, notBefore: string, notOnOrAfter: string, hospitalized: boolean, invoicing: boolean }) {

    try {
      const response = (await Promise.all([
        api.queryMemberData(payload.ssin, payload.io, payload.io_membership, payload.notBefore, payload.notOnOrAfter, payload.hospitalized, payload.invoicing),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];

      commitAddNotification(context, { content: String(i18n.t('patients.dataRetrieved')), color: 'success' });
      console.log(response.data)
      return response.data;
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? error.response.data.detail ?? '';
        if (error.response.status == 401) {
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        else if (error_message.startsWith('The INSS is incorrect')) {
          error_message = String(i18n.t('patients.inssIncorrect'))
        }
        else if (error_message.startsWith('The INSS has an invalid format')) {
          error_message = String(i18n.t('patients.inssIncorrect'))
        }
        else if (error_message.startsWith('Inss not found on Routing algorithm')) {
          error_message = String(i18n.t('patients.inssNotFoundAtMutuality'))
        }
        else if (error_message == 'Multiple errors: The value of contactType is hospitalized and member is not hospitalized, Niss not known in IO') {
          error_message = String(i18n.t('patients.notHospitalized'))
        }
        else if (error_message == 'The value of contactType is hospitalized and member is not hospitalized') {
          error_message = String(i18n.t('patients.notHospitalized'))
        }
        const error_message_detail = error.response.data.detail.detail;
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
      return null;
    }
  },
  async consultEAgreement(context: MainContext, payload: { patientId: number, useInsurancymembership: boolean }) {
    try {
      await Promise.all([
        api.consultEAgreement(payload.patientId, payload.useInsurancymembership),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ])
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? '';
        if (error.response.status == 401) {
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        const error_message_detail = error.response.data.detail.detail;
        if (error.response.status == 400) {
          error_message = parse_error_message(error.response.data.detail)
        }
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
      return []
    }
  },
  async getAgreements(context: MainContext, payload: { patientId: number }) {
    try {
      const response = (await Promise.all([
        api.getAgreements(payload.patientId),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      const p = readOnePatient(context)(payload.patientId);
      if (p) {
        p.agreements = response.data;
        commitSetPatient(context, p)
      }
      return response.data
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? '';
        if (error.response.status == 401) {
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        const error_message_detail = error.response.data.detail.detail;
        if (error.response.status == 400) {
          error_message = parse_error_message(error.response.data.detail)
        }
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
      return []
    }
  },
  async messagesEAgreement(context: MainContext, payload: { patientId: number, get_messages: boolean }) {
    try {
      const response = (await Promise.all([
        api.asyncMessages(payload.patientId, payload.get_messages),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      console.log(response.data)
      return response.data
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? '';
        if (error.response.status == 401) {
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }

        const error_message_detail = error.response.data.detail.detail;
        if (error.response.status == 400) {
          error_message = parse_error_message(error.response.data.detail)
        }
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
    }
  },
  async askEAgreement(context: MainContext, payload: {
    patient_id: number,
    product_or_service: string,
    billable_period?: string,
    serviced_date?: string,
    transaction: string,
    pre_auth_ref?: string,
    attachments: IAttachment[],
    supporting_infos?: string[],
    second_series?: boolean;
    prescription: IPrescriptionInput,
    previous_prescription?: IPrescriptionInput,
    use_insurancy_member: boolean

  }) {
    let request = String(i18n.t('patients.requestTypeApplication'))
    if (payload.transaction == 'claim-extend') {
      request = String(i18n.t('patients.requestTypeExtension'))
    }
    try {
      const response = (await Promise.all([
        api.askEAgreement(payload),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];

      if (response.data.adjudication == 'agreement') {
        commitAddNotification(context, { content: String(i18n.t('patients.requestApproved', { request })), color: 'success' });
      } else if (response.data.status == 'complete' && payload.transaction == 'claim-completeAgreement') {
        commitAddNotification(context, { content: String(i18n.t('patients.additionSuccessful')), color: 'success' });
      } else {
        commitAddNotification(context, { content: String(i18n.t('patients.requestPending', { request })), color: 'success' });
      }
      console.log(response.data)
      return response.data
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? '';
        if (error.response.status == 401) {
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        const error_message_detail = error.response.data.detail.detail;
        if (error.response.status == 400) {
          error_message = parse_error_message(error.response.data.detail, request)
        }
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
    }
  },
  async cancelEAgreement(context: MainContext, payload: {
    patient_id: number,
    product_or_service: string,
    pre_auth_ref: string,
    use_insurancy_member: boolean
  }) {
    const request = String(i18n.t('patients.requestTypeCancellation'))
    try {
      const response = (await Promise.all([
        api.cancelEAgreement(payload),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];

      commitAddNotification(context, { content: String(i18n.t('patients.requestSuccessful', { request })), color: 'success' });
      console.log(response.data)
      return response.data
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? '';
        if (error.response.status == 401) {
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        const error_message_detail = error.response.data.detail.detail;
        if (error.response.status == 400) {
          error_message = parse_error_message(error.response.data.detail, request)
        }
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
    }
  },

  // Pathology counters
  async countEPathology(context: MainContext, payload: { id: number }) {
    try {
      const response = await api.countEPathology(payload.id);
      if (response) {
        return response.data;
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async countFaPathology(context: MainContext, payload: { id: number }) {
    try {
      const response = await api.countFaPathology(payload.id);
      if (response) {
        return response.data;
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async countFbPathology(context: MainContext, payload: { id: number }) {
    try {
      const response = await api.countFbPathology(payload.id);
      if (response) {
        return response.data;
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },

  // Documents
  async addDocument(context: MainContext, payload: { patientId: number, file: File }) {
    try {
      if (!payload.patientId) {
        return
      }
      await api.addDocumentToPatient(payload.patientId, payload.file);
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async deleteDocument(context: MainContext, payload: { documentId: number }) {
    try {
      await api.deleteDocument(payload.documentId);
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async updateDocument(context: MainContext, payload: { documentId: number, file: File }) {
    try {
      const response = await api.updateDocument(payload.documentId, payload.file);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async downloadDocument(context: MainContext, payload: { documentId: number }) {
    try {
      const response = await api.downloadDocument(payload.documentId);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async getDocument(context: MainContext, payload: { documentId: number }) {
    try {
      const response = await api.getDocument(payload.documentId);
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
      return null;
    }
  },
  async getDocumentSize(context: MainContext, payload: { documentId: string, compress: boolean }) {
    try {
      const response = await api.getDocumentSize(payload.documentId, payload.compress);
      console.log(response)
      return response.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async getDocuments(context: MainContext, payload: { patientId: number }) {
    if (payload.patientId == null) {
      return
    }
    try {
      const response = await api.getDocumentsForPatient(payload.patientId);
      if (response) {
        commitSetDocumentsForPatient(context, { patientId: payload.patientId, docs: response.data });
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  // eFact
  async actionSendEFact(context: MainContext, payload: ISendEFactInput) {
    try {
      const response = (await Promise.all([
        api.sendEFact(payload),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];

      commitAddNotification(context, { content: String(i18n.t('efact.requestSuccessful')), color: 'success' });
      console.log(response.data)
      return response.data
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? '';
        if (!error_message) {
          error_message = error.response.data.detail
        }
        if (error.response.status == 401) {
          error_message = String(i18n.t('attestations.eHealthLoginInvalid'))
        }
        const error_message_detail = error.response.data.detail.detail;
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
    }
  },
  async actionGetEFactMessages(context: MainContext) {
    try {
      const response = (await Promise.all([
        api.getEFactMessages(),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      console.log(response.data)
      return response.data
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? '';
        if (!error_message) {
          error_message = error.response.data.detail
        }
        const error_message_detail = error.response.data.detail.detail;
        console.log(error_message)
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
      return []
    }
  },
  async actionCreateEFactCreditNote(context: MainContext, payload: { efactId: number, input: ICreditNoteInput }) {
    try {
      const loadingNotification = { content: String(i18n.t('efact.creatingCreditNote')), showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.creditEFact(payload.efactId, payload.input),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('efact.creditNoteCreated')), color: 'success' });
      return response.data
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail.message ?? '';
        if (!error_message) {
          error_message = error.response.data.detail
        }
        const error_message_detail = error.response.data.detail.detail;
        commitAddNotification(context, { color: 'error', content: error_message, detail: error_message_detail });
      }
      throw error;
    }
  }
};

const { dispatch } = getStoreAccessors<PatientState, State>('');

export const dispatchCreatePatient = dispatch(actions.actionCreatePatient);
export const dispatchClearPatients = dispatch(actions.actionClearPatients);
export const dispatchGetPatient = dispatch(actions.actionGetPatient);
export const dispatchGetMinimalPatient = dispatch(actions.actionGetMinimalPatient);
export const dispatchUpdatePatient = dispatch(actions.actionUpdatePatient);
export const dispatchDisablePatient = dispatch(actions.actionDisablePatient);

export const dispatchCreatePatientComment = dispatch(actions.actionCreateComment);
export const dispatchGetPatientComments = dispatch(actions.actionGetComments);
export const dispatchUpdatePatientComment = dispatch(actions.actionUpdateComment);
export const dispatchDeletePatientComment = dispatch(actions.actionDeleteComment);

export const dispatchQueryMemberData = dispatch(actions.queryMemberEHealth);
export const dispatchConsultEAgreement = dispatch(actions.consultEAgreement);
export const dispatchGetAgreements = dispatch(actions.getAgreements);
export const dispatchMessagesEAgreement = dispatch(actions.messagesEAgreement);

export const dispatchAskEAgreement = dispatch(actions.askEAgreement);
export const dispatchCancelEAgreement = dispatch(actions.cancelEAgreement);

export const dispatchCountEPathology = dispatch(actions.countEPathology);
export const dispatchCountFaPathology = dispatch(actions.countFaPathology);
export const dispatchCountFbPathology = dispatch(actions.countFbPathology);

export const dispatchAddDocumentToPatient = dispatch(actions.addDocument);
export const dispatchGetDocumentsForPatient = dispatch(actions.getDocuments);
export const dispatchDeleteDocument = dispatch(actions.deleteDocument);
export const dispatchUpdateDocument = dispatch(actions.updateDocument);
export const dispatchDownloadDocument = dispatch(actions.downloadDocument);
export const dispatchGetDocument = dispatch(actions.getDocument);
export const dispatchSendEFact = dispatch(actions.actionSendEFact);
export const dispatchGetEFactMessages = dispatch(actions.actionGetEFactMessages);
export const dispatchCreateEFactCreditNote = dispatch(actions.actionCreateEFactCreditNote);
export const dispatchGetDocumentSize = dispatch(actions.getDocumentSize);
